/**
 * Gemeinsam regieren im Zwei-Spieler-Modus: Ressortprinzip und Ressortabstimmung.
 *
 * Ist der Mitspieler Koalitionspartner, darf jede Seite nur Gesetze für Ministerien beauftragen,
 * die ihre Partei führt. Jeder neue Entwurf geht zuerst an die andere Seite (Stufe „coord“):
 * Sie stimmt zu, legt einen Gegenentwurf mit anderen Stellschrauben vor oder lehnt ab.
 * Erst nach Zustimmung schreibt das Ministerium. Abgestimmte Entwürfe kann der Partner
 * im Kabinett nicht mehr blockieren.
 */
import { CAP } from '../content/consts';
import { TPL } from '../content/issues';
import { RMAP } from '../content/ressorts';
import { ISS } from '../content/states';
import { draftInfo } from './ministers';
import { PN } from './rules';
import { S, log } from './state';
import { clamp } from './util';
import { humanPartners, me, isChan, playerByPid } from './versus';
import { spendingFreeze } from './fiscal';
import type { Law, PartyId } from './types';

/** Regiert der Host gerade mit dem Mitspieler als Koalitionspartner? */
export const coopGov = (): boolean => humanPartners().length > 0;
/** Erster Spieler-Partner (für die Ressortabstimmung von Entwürfen des Kanzlers). */
export const partnerId = (): PartyId | null => humanPartners()[0] ?? null;
/** Partei des handelnden Spielers, wenn er Koalitionspartner ist. */
export const myPartnerPid = (): PartyId | null => { const P = me(); return P && !isChan(P.role) && humanPartners().includes(P.pid) ? P.pid : null; };
const other = (p: PartyId): PartyId => (p === 'YOU' ? partnerId() ?? 'YOU' : 'YOU');

/** Welche Partei führt das Ministerium, das für diesen Ansatz zuständig ist? */
export const ressOwnerOf = (T: any): PartyId => (S.ress && S.ress[RMAP[T.r]]) || 'YOU';

/** Darf diese Partei einen Entwurf nach diesem Ansatz beauftragen? */
export function canDraft(party: PartyId, T: any): boolean {
  if (!coopGov()) return party === 'YOU';
  if (T.custom && party !== 'YOU') return false; // Baukasten nur für den Host
  const o = ressOwnerOf(T);
  return party === 'YOU' ? !humanPartners().includes(o) : o === party;
}
export const issueDraftable = (party: PartyId, iss: string): boolean => !!ISS[iss] && ISS[iss].laws.some((T: any) => canDraft(party, T));

export const lawOwner = (L: Law): PartyId => (L.by as PartyId) || 'YOU';
const ACTIVE = ['coord', 'draft', 'ready', 'bt', 'br', 'vm'];
/** Vorhaben in Arbeit je Partei. Im gemeinsamen Regieren hat jede Seite ihr eigenes Kontingent. */
export const busyOf = (party: PartyId): number => S.laws.filter((L) => ACTIVE.includes(L.stage) && (coopGov() ? lawOwner(L) === party : true)).length;

/** Stellschrauben auf gültige Werte bringen. */
function sanitize(T: any, p: Record<string, unknown>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const pa of T.ps) {
    let v = Number(p?.[pa.id]);
    if (!Number.isFinite(v)) v = pa.d;
    if (pa.k === 'r') v = clamp(Math.round(v / pa.st) * pa.st, pa.min, pa.max);
    else if (pa.k === 'c') v = clamp(Math.round(v), 0, pa.o.length - 1);
    else v = v ? 1 : 0;
    out[pa.id] = v;
  }
  return out;
}

function setParams(L: Law, T: any, p: Record<string, number>) {
  const fx = T.fx(p), di = draftInfo(T, fx);
  L.p = p; L.fx = fx; L.br = T.br || (fx.gg ? 1 : 0); L.q = di.q; L.rid = di.rid;
  L.months = Math.max(1, di.months - (di.delay || 0)); // abgestimmt: kein Verschleppen durch den Partner
}

/** Neuen oder überarbeiteten Entwurf in die Ressortabstimmung geben. */
export function startCoord(L: Law, by: PartyId) {
  L.by = by; L.stage = 'coord'; L.left = 0; L.delay = 0; L.agreed = false; L.why = null; L.vote = null;
  setParams(L, TPL(L.iss!, L.tpl || 0), L.p as Record<string, number>); // Bearbeitungszeit neu berechnen
  L.coord = { wait: other(by), round: 1 }; L.pPrev = null;
  log(`${PN(by)} legt „${L.t}“ zur Ressortabstimmung vor. ${PN(other(by))} ist am Zug.`);
}

/** Mitspieler beauftragt einen Entwurf für eines seiner Ministerien. */
export function p2Propose(iss: string, tpl: number, p: Record<string, unknown>) {
  const pid = myPartnerPid(); if (!pid || S.phase !== 'govern') return;
  const st = S.issues[iss]; if (!st || !st.on || st.law) return;
  const T = TPL(iss, tpl || 0); if (!T || !canDraft(pid, T)) return;
  if (busyOf(pid) >= CAP) return;
  if (spendingFreeze(T.fx(sanitize(T, p)))) return;
  const q = sanitize(T, p);
  const L: Law = { id: S.nid++, iss, tpl: tpl || 0, t: T.t, r: T.r, br: 0, p: {}, fx: T.fx(q), stage: 'coord', left: 0 };
  setParams(L, T, q);
  S.laws.push(L); st.law = L.id;
  startCoord(L, pid);
  const P = playerByPid(pid); if (P) P.plan = null;
}

/** Antwort in der Ressortabstimmung: zustimmen, Gegenentwurf, ablehnen oder (nur Urheber) zurückziehen. */
export function coordReply(actor: PartyId, lawId: number, kind: 'ok' | 'counter' | 'reject' | 'withdraw', p?: Record<string, unknown>) {
  if (!coopGov()) return;
  const L = S.laws.find((x) => x.id === lawId); if (!L || L.stage !== 'coord' || !L.coord) return;
  const T = TPL(L.iss!, L.tpl || 0);
  if (kind === 'withdraw') {
    if (lawOwner(L) !== actor) return;
    S.laws = S.laws.filter((x) => x !== L); if (L.iss && S.issues[L.iss]) S.issues[L.iss].law = null;
    log(`${PN(actor)} zieht „${L.t}“ zurück.`); return;
  }
  if (L.coord.wait !== actor) return;
  if (kind === 'ok') {
    L.stage = 'draft'; L.left = L.months || 2; L.agreed = true; L.coord = null;
    log(`${PN(actor)} stimmt „${L.t}“ zu. Das Ministerium ${L.r} schreibt den Entwurf.`, 'good');
  } else if (kind === 'counter') {
    const q = sanitize(T, p || {});
    if (JSON.stringify(q) === JSON.stringify(L.p)) return; // kein Gegenentwurf ohne Änderung
    L.pPrev = L.p; setParams(L, T, q);
    L.coord = { wait: other(actor), round: (L.coord.round || 1) + 1 };
    log(`${PN(actor)} legt einen Gegenentwurf zu „${L.t}“ vor. ${PN(other(actor))} ist am Zug.`);
  } else if (kind === 'reject') {
    L.stage = 'blocked'; L.coord = null; L.why = `${PN(actor)} lehnt den Entwurf in der Ressortabstimmung ab.`;
    S.coal = clamp(S.coal - 3, 0, 100);
    log(`Ressortabstimmung: ${PN(actor)} lehnt „${L.t}“ ab.`, 'bad');
  }
}

/** Der Urheber bringt seinen fertigen Entwurf ins Kabinett. */
export const ownsLaw = (party: PartyId, L: Law | undefined): boolean => !!L && (!coopGov() ? party === 'YOU' : lawOwner(L) === party);

/** Wartet ein Entwurf auf eine Antwort dieser Partei? */
export const coordWaiting = (party: PartyId): Law[] => S.laws.filter((L) => L.stage === 'coord' && L.coord && L.coord.wait === party);

/** Neuer Entwurf einer Partei (z. B. vom Autopiloten der Computer-Regierung). */
export function newLaw(by: PartyId, iss: string, tpl: number, p: Record<string, unknown>): Law | null {
  const st = S.issues[iss]; if (!st || !st.on || st.law) return null;
  const T = TPL(iss, tpl || 0); if (!T || !canDraft(by, T)) return null;
  const q = T.custom ? (p as Record<string, number>) : sanitize(T, p);
  const L: Law = { id: S.nid++, iss, tpl: tpl || 0, t: T.t, r: T.r, br: 0, p: {}, fx: T.fx(q), stage: 'draft', left: 0 };
  setParams(L, T, q);
  if (spendingFreeze(L.fx)) { S.nid--; return null; }
  S.laws.push(L); st.law = L.id;
  if (coopGov()) startCoord(L, by);
  else { L.left = L.months || 2; log(`Ministerium ${T.r} beginnt mit „${L.t}“.`); }
  return L;
}
